import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { HTMLAttributes, useState } from "react";
import {
    SignatureReason,
    signAndLockReport,
    SignAndLockResponseFromAPI,
} from "../../../api/report";
import useTestId from "../../../hooks/useTestId";
import { cn } from "../../../utils";
import Loader from "../../AppLoader";
import { useAuth } from "../../../context/AuthContext";
import { useReport } from "../../../hooks/useReport";
import ReportMFADialog from "./ReportMFADialog";

interface SignatureReasonMenuProps extends HTMLAttributes<HTMLButtonElement> {
    onSignError?: (error: any) => void;
    onSignSuccess?: () => void;
}

export const SignatureReasonMenu: React.FC<SignatureReasonMenuProps> = ({
    onSignError,
    onSignSuccess,
    ...props
}) => {
    const { mutate, status } = useReport();
    const { isAllowed } = useAuth();
    const [isSigning, setIsSigning] = useState(false);
    const [showMFA, setShowMFA] = useState(false);
    const [currentReason, setCurrentReason] = useState<SignatureReason | null>(
        null
    );
    const testId = useTestId();
    const disabled = status === "Signed" || !isAllowed("Report sign");

    const signatureReasons: SignatureReason[] = [
        "Review",
        "Approval",
        "Responsibility",
        "Authorship",
    ];

    const handleSignWithReason = async (reason: SignatureReason) => {
        setIsSigning(true);

        try {
            const response = (await signAndLockReport({
                testId: testId!,
                reason,
            })) as SignAndLockResponseFromAPI;

            // Check if 2FA is required
            if ("code" in response && response.code === "OTP_sent") {
                setCurrentReason(reason);
                setShowMFA(true);
            } else if ("status" in response) {
                // Direct success without 2FA
                mutate(response, false);
                onSignSuccess?.();
            }
        } catch (error) {
            console.error("Error signing report:", error);
            onSignError?.(error);
        } finally {
            setIsSigning(false);
        }
    };

    return (
        <Menu>
            <MenuButton
                {...props}
                className={cn(
                    "btn-primary flex items-center gap-2",
                    props.className
                )}
                disabled={disabled || isSigning}
            >
                {status === "Signed"
                    ? "Signed and locked"
                    : status === "Edited"
                    ? "Correct sign and lock"
                    : "Sign and lock"}
                {isSigning ? (
                    <div className="size-5">
                        <Loader />
                    </div>
                ) : status !== "Signed" ? (
                    <ChevronDownIcon className="data-active:rotate-180 size-5" />
                ) : null}
            </MenuButton>
            <MenuItems
                transition
                anchor="bottom end"
                className="w-52 origin-top-right rounded-xl border border-gray-200 bg-white p-1 text-sm/6 transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
            >
                {signatureReasons.map((reason) => (
                    <MenuItem key={reason}>
                        <button
                            onClick={() => handleSignWithReason(reason)}
                            className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]:bg-black/5"
                            disabled={isSigning}
                        >
                            {reason}
                        </button>
                    </MenuItem>
                ))}
            </MenuItems>
            {currentReason && (
                <ReportMFADialog
                    isOpen={showMFA}
                    testId={testId!}
                    reason={currentReason}
                    onSuccess={(report) => {
                        mutate(report, false);
                        setShowMFA(false);
                        onSignSuccess?.();
                    }}
                    onClose={() => {
                        setShowMFA(false);
                        setCurrentReason(null);
                    }}
                    onResendCode={async () => {
                        await signAndLockReport({
                            testId: testId!,
                            reason: currentReason,
                        });
                    }}
                />
            )}
        </Menu>
    );
};
